/**
 * IBANInput — F-002 Phase 4 (IMPL-P4-004).
 *
 * IBAN input con auto-uppercase, raggruppamento display in blocchi di 4
 * caratteri (solo per la presentazione: il valore esposto via `onChange` è
 * sempre normalizzato — uppercase + niente spazi), e validazione debounced
 * 300ms via `parseIbanIT()` da `@app/shared`.
 *
 * Mirror del pattern di {@link CFInput}:
 *  - trailing icon Check / X / Loader2
 *  - messaggio inline con `aria-live="polite"`
 *  - controlled component, parent owns `value`
 *
 * Il valore tenuto nel form (RHF) NON contiene spazi: il display formattato
 * è solo grafica — il submit invia l'IBAN normalizzato.
 */
import { useEffect, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { parseIbanIT } from '@app/shared';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';

type ValidationStatus = 'idle' | 'validating' | 'valid' | 'invalid';

export interface IBANInputProps
  extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange'> {
  /** IBAN normalizzato (uppercase, no spazi). */
  value: string;
  /** Riceve il valore normalizzato (uppercase, no spazi). */
  onChange: (value: string) => void;
  error?: string;
  id?: string;
}

/** Inserisce uno spazio ogni 4 caratteri solo per la presentazione. */
function groupIbanForDisplay(normalized: string): string {
  return normalized.replace(/(.{4})/g, '$1 ').trim();
}

export function IBANInput({
  value,
  onChange,
  error,
  id,
  className,
  placeholder,
  ...rest
}: IBANInputProps) {
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>('idle');
  const [validationReason, setValidationReason] = useState<string | undefined>();

  useEffect(() => {
    if (!value) {
      setValidationStatus('idle');
      setValidationReason(undefined);
      return;
    }

    setValidationStatus('validating');
    setValidationReason(undefined);

    const timer = setTimeout(() => {
      const parsed = parseIbanIT(value);
      if (parsed.valid) {
        setValidationStatus('valid');
        setValidationReason(undefined);
      } else {
        setValidationStatus('invalid');
        setValidationReason(parsed.reason ?? 'IBAN non valido');
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [value]);

  const inlineMessage = error ?? (validationStatus === 'invalid' ? validationReason : undefined);

  return (
    <div className="space-y-1">
      <div className="relative">
        <Input
          id={id}
          // Display formattato a gruppi di 4 (solo presentazione).
          value={groupIbanForDisplay(value)}
          onChange={(e) => {
            const normalized = e.target.value.toUpperCase().replace(/\s+/g, '');
            onChange(normalized);
          }}
          placeholder={placeholder ?? 'IT60 X054 2811 1010 0000 0123 456'}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          inputMode="text"
          maxLength={34} // 27 char + 6 spazi separator
          aria-invalid={validationStatus === 'invalid' || Boolean(error)}
          className={cn('pr-9 font-mono tracking-wide uppercase', className)}
          {...rest}
        />
        <span
          className="pointer-events-none absolute inset-y-0 right-2 flex items-center"
          aria-hidden="true"
        >
          {validationStatus === 'validating' ? (
            <Loader2
              data-testid="icon-loader"
              className="h-4 w-4 animate-spin text-muted-foreground"
            />
          ) : validationStatus === 'valid' ? (
            <Check data-testid="icon-check" className="h-4 w-4 text-green-600" />
          ) : validationStatus === 'invalid' ? (
            <X data-testid="icon-x" className="h-4 w-4 text-destructive" />
          ) : null}
        </span>
      </div>

      <p
        role="status"
        aria-live="polite"
        className={cn(
          'min-h-[1rem] text-xs',
          inlineMessage ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {inlineMessage ?? (validationStatus === 'valid' ? 'IBAN valido' : '')}
      </p>
    </div>
  );
}
